const _excluded = ["remote", "module", "exports", "fallback", "fallbackUrl"];
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable react-hooks/rules-of-hooks, react/display-name, security/detect-object-injection,import/no-unresolved */

import React from "react";
import PropTypes from "prop-types";
import stringifyDeterministic from "json-stringify-deterministic";
import sortRecursive from "sort-keys-recursive";
import { injectScript } from "@module-federation/utilities";
import { FederationBoundary } from "@module-federation/utilities/src/utils/react";
import { captureException } from "@lululemon/mwa-utils";
import ErrorBoundary from "../common/ErrorBoundary";
import { loadJsFileAsync } from "./index";
import createFetchFederatedComponentCtx from "./server-fetch-ctx";
const publicConfigContainer = {};

// generate id for component (legacy)
function createId(remote, module, props) {
  return stringifyDeterministic(sortRecursive({
    module,
    props,
    remote
  }));
}
const fetchPrerenderUrl = (prerenderUrl, remote) => {
  return fetch(prerenderUrl).then(res => {
    return res.json();
  }).catch(() => {
    const url = new URL(prerenderUrl);
    url.searchParams.append("asScript", "1");
    return loadJsFileAsync(url.href);
  }).then(config => {
    if (window[`PUBLIC_CONFIG_${remote}`]) {
      publicConfigContainer[remote] = 1;
    } else if (config) {
      publicConfigContainer[remote] = 1;
      window[`PUBLIC_CONFIG_${remote}`] = config;
    } else {
      captureException(new Error(`Failed to get ${remote} public config`));
    }
  });
};
const loadSentryHub = remote => {
  var _window$sentryHub;
  return (_window$sentryHub = window.sentryHub) !== null && _window$sentryHub !== void 0 && _window$sentryHub[remote] ? Promise.resolve() : window[remote].get("./sentry").catch(e => {
    console.error(e);
    return Promise.resolve();
  });
};

/**
 *
 * @param {string} remote - the remote global name
 * @param {object | string} shareScope - the shareScope Object OR scope key
 * @param {string} remoteFallbackUrl - fallback url for remote module
 * @returns {Promise<React.Component>} - Federated Module
 */
const getOrLoadRemote = (remote, shareScope, remoteFallbackUrl = undefined) =>
// TODO: reduce cognitive complexity
// eslint-disable-next-line sonarjs/cognitive-complexity
new Promise(async (resolve, reject) => {
  // check if remote exists on window
  if (!window[remote]) {
    // search dom to see if remote tag exists, but might still be loading (async)
    const existingRemote = document.querySelector(`[data-webpack="${remote}"]`);
    // when remote is loaded..
    const onload = async () => {
      // check if it was initialized
      if (!window[remote].__initialized) {
        // if share scope doesnt exist (like in webpack 4) then expect shareScope to be a manual object
        if (typeof __webpack_share_scopes__ === "undefined") {
          // use default share scope object passed in manually
          await window[remote].init(shareScope.default || shareScope);
        } else {
          // otherwise, init share scope as usual
          await window[remote].init(__webpack_share_scopes__.default);
        }
        // mark remote as initialized
        window[remote].__initialized = true;
      }
      // resolve promise so marking remote as loaded
      resolve();
    };
    if (existingRemote) {
      // if existing remote but not loaded, hook into its onload and wait for it to be ready
      existingRemote.onload = onload;
      existingRemote.onerror = reject;
      // if remote is in DOM, but federatedComponent is called AFTER the remote loaded, it must be initialized
      if (window[remote] && !window[remote].__initialized) {
        if (typeof __webpack_share_scopes__ === "undefined") {
          // use default share scope object passed in manually
          await window[remote].init(shareScope.default || shareScope);
        } else {
          // otherwise, init share scope as usual
          await window[remote].init(__webpack_share_scopes__.default);
        }
        window[remote].__initialized = true;
      }
      // check if remote fallback exists as param passed to function
      // TODO: should scan public config for a matching key if no override exists
    } else if (remoteFallbackUrl) {
      // inject remote if a fallback exists and call the same onload function
      var d = document,
        script = d.createElement("script");
      script.type = "text/javascript";
      // mark as data-webpack so runtime can track it internally
      script.setAttribute("data-webpack", `${remote}`);
      script.async = true;
      script.onerror = reject;
      script.onload = onload;
      script.src = remoteFallbackUrl;
      d.getElementsByTagName("head")[0].appendChild(script);
    } else {
      // no remote and no fallback exist, reject
      reject(`Cannot Find Remote ${remote} to inject`);
    }
    return;
  }
  // remote already instantiated, resolve
  resolve();
});
// eslint-disable-next-line sonarjs/cognitive-complexity
const createWindowFederatedComponentCtx = ({
  remotes
}, previous) => {
  const context = _extends({
    components: {},
    chunks: []
  }, previous, {
    promises: previous && previous.promises && previous.promises.has ? previous.promises : new Map(),
    async loadFederatedComponent(id, remote, module, props, shareScope, remoteFallbackUrl) {
      if (context.promises.has(id)) {
        return;
      }
      const prerenderUrl = remotes && remotes[remote] && remotes[remote].prerender;
      if (prerenderUrl) {
        publicConfigContainer[remote] = publicConfigContainer[remote] || fetchPrerenderUrl(prerenderUrl, remote);
      }
      try {
        await getOrLoadRemote(remote, shareScope, remoteFallbackUrl);
      } catch (err) {
        // remote was rejected
        captureException(err);
        context.components[id] = () => null;
      }
      const componentPromise = Promise.all([publicConfigContainer[remote], Promise.resolve(
      // initialize if we havent already, only initialize if it exists in the first place
      typeof __webpack_init_sharing__ !== "undefined" && !__webpack_share_scopes__.default ? __webpack_init_sharing__("default") : null).catch(() => null).then(() => {
        try {
          if (!window[remote].__initialized) {
            if (typeof __webpack_share_scopes__ !== "undefined") {
              window[remote].__initialized = true;
              return window[remote].init(__webpack_share_scopes__.default);
            } else {
              window[remote].__initialized = true;
              return window[remote].init(shareScope.default || shareScope);
            }
          }
        } catch (e) {
          // some err
          console.error("backup remote init failed", e);
        }
        return null;
      }).then(() => {
        return window[remote].get(module);
      }).catch(() => {
        context.components[id] = () => null;
      }), loadSentryHub(remote)]).then(promises => {
        // Execute sentry
        Promise.resolve(promises[2]).then(factory => {
          factory && factory();
        }).catch(e => {
          console.error(e);
          captureException(e);
        });
        // Executing remote module
        return Promise.resolve(promises[1]).then(factory => {
          if (!factory) {
            throw new Error("Module not found");
          }
          return factory();
        }).then(mod => mod && mod.default || mod).then(mod => {
          context.components[id] = mod.default || mod;
        }).catch(err => {
          // component failed for some reason
          console.error(err);
          captureException(err);
          context.components[id] = () => null;
        });
      });
      context.promises.set(id, componentPromise);
      return componentPromise;
    }
  });
  return context;
};
const getOrCreateFederatedComponentCtx = !process.browser ? createFetchFederatedComponentCtx : createWindowFederatedComponentCtx;
const federatedComponentsContext = /*#__PURE__*/React.createContext();
const windowFederatedComponent = (remote, module, shareScope, globalContext, remoteFallbackUrl, Fallback) => {
  return props => {
    try {
      var _federatedContext, _federatedContext$com;
      let federatedContext = React.useContext(federatedComponentsContext);
      if (!federatedContext && !window.failoverFcc) {
        console.error("multiple copies of mwa-common might be installed, state is tearing");
        federatedContext = window.failoverFcc = createWindowFederatedComponentCtx({
          remotes: window[`PUBLIC_CONFIG_${process.env.CURRENT_HOST}`].remoteEntries
        });
      } else if (window.failoverFcc && !federatedContext) {
        federatedContext = window.failoverFcc;
      }
      const id = createId({
        module,
        remote
      });
      const Component = (_federatedContext = federatedContext) === null || _federatedContext === void 0 ? void 0 : (_federatedContext$com = _federatedContext.components) === null || _federatedContext$com === void 0 ? void 0 : _federatedContext$com[id];
      const [mounted, setMounted] = React.useState();
      React.useEffect(() => {
        var _federatedContext2;
        if ((_federatedContext2 = federatedContext) !== null && _federatedContext2 !== void 0 && _federatedContext2.loadFederatedComponent) {
          federatedContext.loadFederatedComponent(id, remote, module, props, shareScope, remoteFallbackUrl).then(() => {
            setMounted(true);
          });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      if (!mounted || !Component) {
        if (Fallback) {
          return /*#__PURE__*/React.createElement(Fallback, props);
        }
        return /*#__PURE__*/React.createElement("div", {
          style: {
            display: "contents"
          },
          dangerouslySetInnerHTML: {
            __html: ""
          },
          suppressHydrationWarning: true
        });
      }
      return /*#__PURE__*/React.createElement(ErrorBoundary, {
        remote,
        componentName: module
      }, /*#__PURE__*/React.createElement(Component, _extends({}, props, {
        globalContext
      })));
    } catch (e) {
      console.error(e);
      return null;
    }
  };
};
const safeInjectRemote = props => {
  try {
    return injectScript(props);
  } catch (e) {
    return Promise.reject(e);
  }
};
const federatedModule = props => {
  return safeInjectRemote(props.remote).catch(e => safeInjectRemote({
    global: props.remote,
    url: props.fallbackUrl
  })).then(container => {
    const moduleToGet = props.module.startsWith(".") ? props.module : `./${props.module}`;
    return container.get(moduleToGet).then(factory => factory()).then(m => {
      if (props.exports) {
        return m[props.exports];
      }
      return m.default || m;
    });
  });
};
const FederatedComponent = _ref => {
  let {
      remote,
      module = ".",
      exports,
      fallback,
      fallbackUrl
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!process.browser) {
    return null;
  }
  if (!remote || !module) {
    return null;
  }
  return /*#__PURE__*/React.createElement(FederationBoundary, _extends({
    dynamicImporter: () => federatedModule({
      remote,
      module,
      exports,
      fallback,
      fallbackUrl
    }),
    fallback: () => fallback,
    customBoundary: ErrorBoundary
  }, rest));
};
FederatedComponent.propTypes = {
  remote: PropTypes.string.isRequired,
  module: PropTypes.string.isRequired,
  fallback: PropTypes.elementType,
  fallbackUrl: PropTypes.string
};
FederatedComponent.defaultProps = {
  fallback: null,
  fallbackUrl: null
};
function federatedComponent(args) {
  console.warn("federatedComponent is deprecated, use FederatedComponent instead");
  try {
    const funcArgs = typeof arguments[0] === "object" ? args : {
      remote: arguments[0],
      module: arguments[1],
      shareScope: arguments[2] || "default",
      globalContext: arguments[3],
      remoteFallbackUrl: arguments[4],
      fallback: arguments[5],
      ssr: arguments[6] || true
    };
    let {
      remote,
      module,
      shareScope = "default",
      globalContext,
      remoteFallbackUrl,
      fallback: Fallback
    } = funcArgs;
    if (!process.browser) {
      return () => null;
    }
    return windowFederatedComponent(remote, module, shareScope, globalContext, remoteFallbackUrl, Fallback);
  } catch (e) {
    console.error(e);
    captureException(e);
    return () => null;
  }
}
export { getOrCreateFederatedComponentCtx, federatedComponentsContext, federatedComponent, publicConfigContainer, FederatedComponent };